import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import type { Scenario } from '@rc/engine/types';

interface Props {
  scenarios: Scenario[];
  currentId: string;
  onPick: (id: string) => void;
}

export default function ScenarioPicker({ scenarios, currentId, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const current = scenarios.find((s) => s.id === currentId);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="btn-secondary min-w-[180px] justify-between">
        <span className="truncate">{current?.name || 'Select scenario…'}</span>
        <ChevronDown size={14} className={clsx('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 card w-72 z-30 max-h-80 overflow-y-auto">
          <ul className="py-1">
            {scenarios.map((s) => (
              <li key={s.id}>
                <button
                  className={clsx(
                    'w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-slate-50 text-sm',
                    s.id === currentId && 'bg-brand-purple-50/50',
                  )}
                  onClick={() => {
                    onPick(s.id);
                    setOpen(false);
                  }}
                >
                  <div className="w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center">
                    {s.id === currentId && <Check size={14} className="text-brand-purple-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{s.name}</div>
                    {s.notes && (
                      <div className="text-xs text-slate-500 truncate">{s.notes}</div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
